/**
 * Una base IndexedDB mínima, con promesas (#864).
 *
 * <p>Sin librería: el uso es chico —dos almacenes, lectura y escritura por
 * clave— y cada dependencia nueva es algo más que mantener en la tablet. Guarda
 * `Blob` tal cual, que es lo que necesitan las fotos.
 */
export class BaseLocal {

  static readonly OPERACIONES = 'operaciones';
  static readonly BORRADORES = 'borradores';
  private static readonly VERSION = 1;

  private constructor(private readonly db: IDBDatabase, readonly nombre: string) {}

  static abrir(nombre: string): Promise<BaseLocal> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(nombre, BaseLocal.VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(BaseLocal.OPERACIONES)) {
          db.createObjectStore(BaseLocal.OPERACIONES, { keyPath: 'clientOpId' });
        }
        if (!db.objectStoreNames.contains(BaseLocal.BORRADORES)) {
          db.createObjectStore(BaseLocal.BORRADORES, { keyPath: 'clave' });
        }
      };
      req.onsuccess = () => {
        const db = req.result;
        // Otra pestaña la borra (logout) o la actualiza: se suelta, en vez de
        // dejar ese pedido —y todas las aperturas detrás— esperando.
        db.onversionchange = () => db.close();
        resolve(new BaseLocal(db, nombre));
      };
      req.onerror = () => reject(req.error);
    });
  }

  /** Borra la base entera. Espera a que no quede ninguna conexión abierta. */
  static eliminar(nombre: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.deleteDatabase(nombre);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      // Otra pestaña con la base abierta: se completa cuando la cierre.
      req.onblocked = () => resolve();
    });
  }

  cerrar(): void {
    this.db.close();
  }

  todos<T>(almacen: string): Promise<T[]> {
    return this.pedir<T[]>(almacen, 'readonly', s => s.getAll());
  }

  leer<T>(almacen: string, clave: string): Promise<T | undefined> {
    return this.pedir<T | undefined>(almacen, 'readonly', s => s.get(clave));
  }

  guardar<T>(almacen: string, valor: T): Promise<void> {
    return this.pedir<IDBValidKey>(almacen, 'readwrite', s => s.put(valor)).then(() => undefined);
  }

  borrar(almacen: string, clave: string): Promise<void> {
    return this.pedir<undefined>(almacen, 'readwrite', s => s.delete(clave)).then(() => undefined);
  }

  contar(almacen: string): Promise<number> {
    return this.pedir<number>(almacen, 'readonly', s => s.count());
  }

  private pedir<T>(almacen: string, modo: IDBTransactionMode,
                   operacion: (s: IDBObjectStore) => IDBRequest): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(almacen, modo);
      const req = operacion(tx.objectStore(almacen));
      // Se resuelve al completar la transacción, no el pedido: una escritura
      // recién es durable cuando la transacción terminó.
      tx.oncomplete = () => resolve(req.result as T);
      tx.onerror = () => reject(tx.error ?? req.error);
      tx.onabort = () => reject(tx.error ?? req.error);
    });
  }
}
